import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApplyPortalConfigResponse, ApplyPortalConfigUpdatePayload } from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase H2 FE — TanStack Query bindings for the apply portal lifecycle
 * config (`GET` / `PUT /admissions/apply-portal-config`).
 *
 * The IA self-serve editor at `/settings/admissions-lifecycle` reads
 * the `Response`, builds an `UpdatePayload` locally, and ships the
 * entire config on save. Wholesale-replace (rather than partial PATCH)
 * is intentional — the BE's consistency validators
 * (`schedule_kind='scheduled'` requires ≥1 window; window time-ordering;
 * tz-aware datetimes) are easier to enforce against a complete payload
 * than against a diff.
 *
 * Cache-key contract: a single `["apply-portal-config"]` key per
 * tenant/session (the IA principal is always tenant-scoped, so the
 * key is unambiguous). On a successful PUT we both `setQueryData` (so
 * the IA dashboard chip flips immediately) and invalidate (so any
 * derived queries refetch — e.g., the admissions dashboard's "open
 * window" hint).
 */

export const applyPortalConfigKey = ["apply-portal-config"] as const;

export function useApplyPortalConfig() {
  return useQuery<ApplyPortalConfigResponse>({
    queryKey: applyPortalConfigKey,
    queryFn: () => api.get<ApplyPortalConfigResponse>("/admissions/apply-portal-config"),
    staleTime: 30_000,
  });
}

export function useUpdateApplyPortalConfig() {
  const qc = useQueryClient();
  return useMutation<ApplyPortalConfigResponse, Error, ApplyPortalConfigUpdatePayload>({
    mutationKey: [...applyPortalConfigKey, "update"],
    mutationFn: (payload) =>
      api.put<ApplyPortalConfigResponse>("/admissions/apply-portal-config", payload),
    onSuccess: (response) => {
      qc.setQueryData(applyPortalConfigKey, response);
      void qc.invalidateQueries({ queryKey: applyPortalConfigKey });
    },
  });
}
